import { useEffect } from "react";
import { Platform } from "react-native";

type Target = HTMLElement | string | null | undefined;

function resolveContainer(target?: Target): HTMLElement | null {
  if (typeof target === "string")
    return document.querySelector<HTMLElement>(target);
  if (target instanceof HTMLElement) return target;
  return (document.scrollingElement as HTMLElement) || document.documentElement;
}

export function useLockScroll(locked: boolean, opts?: { container?: Target }) {
  useEffect(() => {
    if (Platform.OS !== "web" || !locked) return;

    const html = document.documentElement;
    const body = document.body;
    const scrollEl = resolveContainer(opts?.container) || html;
    const isRoot = scrollEl === html || scrollEl === body;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    // current scroll position of the *actual* scroller
    const prevScrollTop = isRoot
      ? window.scrollY || window.pageYOffset
      : scrollEl.scrollTop;

    let restore = () => {};

    // Width compensation to avoid layout shift when scrollbar disappears
    const _scrollbarWidth = window.innerWidth - html.clientWidth;

    if (!isIOS && isRoot) {
      // Desktop/mobile Chrome path: no-jump overflow lock
      const prevHtmlOverflowY = html.style.overflowY;
      const prevTouchAction = body.style.touchAction;
      html.style.overflowY = "hidden";
      body.style.touchAction = "none";

      restore = () => {
        html.style.overflowY = prevHtmlOverflowY;
        body.style.touchAction = prevTouchAction;
        // no need to scrollTo; position never changed
      };
    } else if (!isRoot) {
      // Lock a custom scroll container
      const prevOverflow = scrollEl.style.overflow;
      // biome-ignore lint/suspicious/noExplicitAny: Because
      const prevOverscroll = (scrollEl.style as any).overscrollBehaviorY;
      scrollEl.style.overflow = "hidden";
      // biome-ignore lint/suspicious/noExplicitAny: Because
      (scrollEl.style as any).overscrollBehaviorY = "none";

      restore = () => {
        scrollEl.style.overflow = prevOverflow;
        // biome-ignore lint/suspicious/noExplicitAny: Because
        (scrollEl.style as any).overscrollBehaviorY = prevOverscroll;
        scrollEl.scrollTop = prevScrollTop;
      };
    } else {
      // iOS (or when position:fixed approach is preferred)
      const prev = {
        position: body.style.position,
        top: body.style.top,
        width: body.style.width,
        overflowY: body.style.overflowY,
        overscroll: html.style.overscrollBehaviorY,
        touchAction: body.style.touchAction,
      };

      body.style.position = "fixed";
      body.style.top = `-${prevScrollTop}px`;
      body.style.width = "100%";
      body.style.overflowY = "hidden";
      body.style.touchAction = "none";
      html.style.overscrollBehaviorY = "none";

      restore = () => {
        body.style.position = prev.position;
        body.style.top = prev.top;
        body.style.width = prev.width;
        body.style.overflowY = prev.overflowY;
        body.style.touchAction = prev.touchAction;
        html.style.overscrollBehaviorY = prev.overscroll;

        // restore previous scroll
        window.scrollTo(0, prevScrollTop);
      };
    }

    return restore;
  }, [locked, opts?.container]);
}
